import { useState, useCallback, useMemo } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";

type Session = {
  id: string;
  title: string | null;
  status: "active" | "archived";
  messageCount: number;
  createdAt: string | Date;
  updatedAt: string | Date;
};

export function CaraSessionList({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewSession,
  onRenameSession,
  onDeleteSession,
  isCreating,
}: {
  sessions: Session[];
  activeSessionId: string | null;
  onSelectSession: (id: string) => void;
  onNewSession: () => void;
  onRenameSession: (id: string, title: string) => void;
  onDeleteSession: (id: string) => void;
  isCreating: boolean;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return sessions;
    const q = searchQuery.toLowerCase();
    return sessions.filter((s) =>
      (s.title ?? "Untitled session").toLowerCase().includes(q),
    );
  }, [sessions, searchQuery]);

  const handleStartRename = useCallback((session: Session) => {
    setEditingId(session.id);
    setEditTitle(session.title || "");
  }, []);

  const handleFinishRename = useCallback(() => {
    if (editingId && editTitle.trim()) {
      onRenameSession(editingId, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle("");
  }, [editingId, editTitle, onRenameSession]);

  const handleDelete = useCallback((id: string) => {
    onDeleteSession(id);
    setConfirmDeleteId(null);
  }, [onDeleteSession]);

  return (
    <div className="flex flex-col h-full">
      {/* New session button + search */}
      <div className="p-3 border-b border-border space-y-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2 text-xs"
          onClick={onNewSession}
          disabled={isCreating}
        >
          {isCreating ? (
            <span className="h-3 w-3 border border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3">
              <path d="M8 2a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 8 2Z" />
            </svg>
          )}
          New Design Session
        </Button>
        {sessions.length > 3 && (
          <div className="relative">
            <svg viewBox="0 0 16 16" fill="currentColor" className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground/50">
              <path fillRule="evenodd" d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z" clipRule="evenodd" />
            </svg>
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search sessions…"
              className="h-7 text-xs pl-7"
            />
          </div>
        )}
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto" role="listbox" aria-label="Design sessions">
        {sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-4 py-8 text-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-8 w-8 text-primary/40 mb-3">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
            </svg>
            <p className="text-sm text-muted-foreground mb-1">No sessions yet</p>
            <p className="text-xs text-muted-foreground/60">Start your first design session with Cara</p>
          </div>
        ) : (
          <div className="py-1">
            {filteredSessions.length === 0 && searchQuery && (
              <p className="text-xs text-muted-foreground/50 text-center py-4">No matching sessions</p>
            )}
            {filteredSessions.map((session) => {
              const isActive = session.id === activeSessionId;
              const isEditing = editingId === session.id;
              const isConfirmingDelete = confirmDeleteId === session.id;

              return (
                <div
                  key={session.id}
                  role="option"
                  aria-selected={isActive}
                  className={`group relative px-3 py-2.5 cursor-pointer transition-colors duration-150 ${
                    isActive
                      ? "bg-primary/10 border-l-2 border-primary"
                      : "hover:bg-muted/50 border-l-2 border-transparent"
                  }`}
                  onClick={() => !isEditing && onSelectSession(session.id)}
                  onKeyDown={(e) => e.key === "Enter" && !isEditing && onSelectSession(session.id)}
                  tabIndex={0}
                >
                  {isEditing ? (
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onBlur={handleFinishRename}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleFinishRename();
                        if (e.key === "Escape") { setEditingId(null); setEditTitle(""); }
                      }}
                      className="h-6 text-xs"
                      autoFocus
                    />
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <div className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                          session.messageCount > 0 ? "bg-emerald-500" : "bg-muted-foreground/30"
                        }`} />
                        <p className="text-xs text-foreground truncate flex-1">
                          {session.title || "Untitled session"}
                        </p>
                      </div>
                      <p className="text-[10px] text-muted-foreground/50 mt-0.5 ml-3.5">
                        {new Date(session.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </p>

                      {/* Actions on hover */}
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        {isConfirmingDelete ? (
                          <>
                            <Button
                              variant="destructive"
                              size="xs"
                              onClick={(e) => { e.stopPropagation(); handleDelete(session.id); }}
                            >
                              Delete
                            </Button>
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <button
                              className="h-5 w-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground transition-colors"
                              onClick={(e) => { e.stopPropagation(); handleStartRename(session); }}
                              aria-label="Rename session"
                            >
                              <svg viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3">
                                <path d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61Z" />
                              </svg>
                            </button>
                            <button
                              className="h-5 w-5 flex items-center justify-center rounded text-muted-foreground hover:text-destructive transition-colors"
                              onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(session.id); }}
                              aria-label="Delete session"
                            >
                              <svg viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3">
                                <path fillRule="evenodd" d="M5 3.25V4H2.75a.75.75 0 0 0 0 1.5h.3l.815 8.15A1.5 1.5 0 0 0 5.357 15h5.285a1.5 1.5 0 0 0 1.493-1.35l.815-8.15h.3a.75.75 0 0 0 0-1.5H11v-.75A2.25 2.25 0 0 0 8.75 1h-1.5A2.25 2.25 0 0 0 5 3.25Zm2.25-.75a.75.75 0 0 0-.75.75V4h3v-.75a.75.75 0 0 0-.75-.75h-1.5Z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
